import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Code, Users, Trophy, BarChart3, RefreshCw, Plus, Star, 
  Edit, Trash2, Eye, CheckCircle, XCircle, Calendar, Loader,
  TrendingUp, Building, Database, Brain, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CodeArenaDashboardAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchDashboard();
    } else if (activeTab === 'problems') {
      fetchProblems();
    }
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/codearena/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      toast.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchProblems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/codearena/problems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProblems(res.data.problems || []);
    } catch (err) {
      toast.error('Failed to fetch problems');
    } finally {
      setLoading(false);
    }
  };

  const seedProblems = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/codearena/seed-problems`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sample problems seeded successfully!');
      fetchProblems();
    } catch (err) {
      toast.error('Failed to seed problems');
    }
  };

  const setPOTD = async (problemId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/codearena/problems/${problemId}/set-potd`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Problem set as today\'s POTD!');
    } catch (err) {
      toast.error('Failed to set POTD');
    }
  };

  const deleteProblem = async (problemId) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/codearena/problems/${problemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Problem deleted');
      fetchProblems();
    } catch (err) {
      toast.error('Failed to delete problem');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'hard': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-cyan-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Code className="text-cyan-600" size={28} />
            EvalentX Arena Dashboard
          </h2>
          <p className="text-gray-600 mt-1">National Developer Talent Platform Administration</p>
        </div>
        <button
          onClick={activeTab === 'overview' ? fetchDashboard : fetchProblems}
          className="p-2 bg-cyan-100 text-cyan-600 rounded-lg hover:bg-cyan-200 transition-colors"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'problems', label: 'Problems', icon: Code },
          { id: 'contests', label: 'Contests', icon: Trophy }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-5 text-white">
              <Users size={24} className="opacity-80 mb-2" />
              <p className="text-3xl font-bold">{dashboard.stats?.total_students || 0}</p>
              <p className="text-cyan-200 text-sm">Total Students</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
              <Code size={24} className="opacity-80 mb-2" />
              <p className="text-3xl font-bold">{dashboard.stats?.total_problems || 0}</p>
              <p className="text-purple-200 text-sm">Total Problems</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
              <CheckCircle size={24} className="opacity-80 mb-2" />
              <p className="text-3xl font-bold">{dashboard.stats?.accepted_submissions || 0}</p>
              <p className="text-emerald-200 text-sm">Accepted Solutions</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
              <TrendingUp size={24} className="opacity-80 mb-2" />
              <p className="text-3xl font-bold">{dashboard.stats?.today_submissions || 0}</p>
              <p className="text-amber-200 text-sm">Today's Activity</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Top Colleges */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building size={18} className="text-cyan-600" />
                Top Participating Colleges
              </h3>
              {dashboard.top_colleges?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.top_colleges.map((college, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="text-gray-700">{college._id}</span>
                      </div>
                      <span className="font-semibold text-cyan-600">{college.count} students</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6">No data yet</p>
              )}
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar size={18} className="text-cyan-600" />
                Recent Submissions
              </h3>
              {dashboard.recent_submissions?.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {dashboard.recent_submissions.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{sub.user_name || 'Student'}</p>
                        <p className="text-xs text-gray-500">{sub.problem_title || sub.problem_id}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        sub.verdict === 'Accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {sub.verdict}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6">No submissions yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Problems Tab */}
      {activeTab === 'problems' && (
        <div className="space-y-4">
          {/* Actions */}
          <div className="flex justify-between items-center">
            <p className="text-gray-600">{problems.length} problems in database</p>
            <div className="flex gap-2">
              <button
                onClick={seedProblems}
                className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
              >
                <Database size={18} />
                Seed Sample Problems
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
              >
                <Plus size={18} />
                Create Problem
              </button>
            </div>
          </div>

          {/* Problems Table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 text-cyan-600 animate-spin" />
              </div>
            ) : problems.length === 0 ? (
              <div className="text-center py-12">
                <Code className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No problems yet. Seed some samples to get started!</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Difficulty</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Points</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Domains</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {problems.map((problem, idx) => (
                    <tr key={problem.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{problem.title}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">{problem.description?.substring(0, 60)}...</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(problem.difficulty)}`}>
                          {problem.difficulty?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-bold text-cyan-600">+{problem.points}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {problem.domains?.slice(0, 2).map((d, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              {d}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {problem.is_active ? (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">Active</span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setPOTD(problem.id)}
                            className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                            title="Set as POTD"
                          >
                            <Star size={16} />
                          </button>
                          <button
                            onClick={() => setEditingProblem(problem)}
                            className="p-1.5 text-cyan-600 hover:bg-cyan-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteProblem(problem.id)}
                            className="p-1.5 text-rose-600 hover:bg-rose-100 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Contests Tab */}
      {activeTab === 'contests' && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="font-semibold text-gray-900 mb-2">Contest Management Coming Soon</h3>
          <p className="text-gray-500">Create and manage coding contests from here.</p>
        </div>
      )}
    </div>
  );
};

export default CodeArenaDashboardAdmin;
